/**
 * LaborItemRow Component
 *
 * Displays a single labor item with status tracking and completion controls.
 * Used in both the editable service detail content and the read-only views.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  InputAdornment,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  MoreVert as MoreVertIcon,
  Pause as PauseIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { useNotify } from '@/hooks/useNotify';
import { lineNetAfterDiscountCents } from '@/utils/money';
import {
  useUpdateLaborItemStatus,
  useStartLaborItem,
  useCompleteLaborItem,
  type BookingServiceLaborItem,
  type LaborItemStatus,
} from '@/hooks/useWorkOrders';
import type { HaynesProAdoptResult } from '@/hooks/useHaynesProWorkOrderTime';
import { HaynesProWorkOrderTimeDialog } from './haynespro/HaynesProWorkOrderTimeDialog';
import { describeApiError } from '@/lib/describeApiError';
import { dateFnsLocale } from '@/utils/locale';
import { useWorkCompletionRequest } from './completion/useWorkCompletionGate';

interface LaborItemRowProps {
  item: BookingServiceLaborItem;
  index: number;
  isEditable?: boolean;
  workOrderId?: string;
  serviceId?: string;
  /**
   * Status of the parent booking service. A running step under a paused
   * service is shown as paused rather than in progress — pausing is a
   * service-level override and resume continues this same step (#1028).
   */
  serviceStatus?: string;
  /**
   * Set when the work order's own state forbids labor-item status commands
   * (vehicle not arrived, or billing-locked / terminal). The API enforces this
   * too; hiding the controls just avoids offering an action that would 400.
   */
  statusActionsLocked?: boolean;
  onUpdate?: <K extends keyof BookingServiceLaborItem>(
    index: number,
    field: K,
    value: BookingServiceLaborItem[K],
  ) => void;
  onRestoreHours?: (index: number) => void;
  onRemove?: (index: number) => void;
  /**
   * Key handler for the whole row (#3276): the parent adds a new step on a
   * plain Enter, mirroring the product rows (#2674). Undefined when steps
   * cannot be added, so Enter stays inert.
   */
  onRowKeyDown?: ((event: KeyboardEvent<HTMLDivElement>) => void) | undefined;
  /** Lets the parent focus the description of a freshly added step (#3276). */
  descriptionInputRef?: ((element: HTMLInputElement | null) => void) | undefined;
  /**
   * Labor-item estimated hours are billable time (#2706). Description, discount
   * and status stay on `isEditable`; hours need `work_orders.edit_billable_hours`.
   */
  hoursEditable?: boolean;
  /** Snapshotted hourly rate (integer cents). Omit or 0 hides the € figure (#1439). */
  laborRateCents?: number | null;
  formatPrice?: (cents: number) => string;
  /** HaynesPro snapshot adoption (#1442). Hidden unless the server reports snapshotsEnabled. */
  haynesProEnabled?: boolean;
  /** Unsaved service drafts or a save in flight — open/adopt is blocked. */
  haynesProBlocked?: boolean;
  vehicleId?: string;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  pricingType?: string | null;
  onHaynesProAdopted?: (result: HaynesProAdoptResult) => void;
}

export const persistedIdPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Round to 2dp and strip trailing zeros, so float remainders (e.g. from a
 * numeric(5,2) round-trip) never show up in the field (#1025). */
function formatHours(hours: number): string {
  if (!Number.isFinite(hours)) return '';
  return Number(hours.toFixed(2)).toString();
}

const statusColors: Record<LaborItemStatus, 'default' | 'primary' | 'success'> = {
  pending: 'default',
  in_progress: 'primary',
  completed: 'success',
};

const statusIcons: Record<LaborItemStatus, React.ReactElement | undefined> = {
  pending: undefined,
  in_progress: <StartIcon fontSize="small" />,
  completed: <CompleteIcon fontSize="small" />,
};

const statusTranslationKeys: Record<LaborItemStatus, string> = {
  pending: 'tasksPending',
  in_progress: 'tasksInProgress',
  completed: 'tasksCompleted',
};

export function LaborItemRow({
  item,
  index,
  isEditable = false,
  hoursEditable = isEditable,
  workOrderId,
  serviceId,
  serviceStatus,
  statusActionsLocked = false,
  onUpdate,
  onRestoreHours,
  onRemove,
  onRowKeyDown,
  descriptionInputRef,
  laborRateCents,
  formatPrice,
  haynesProEnabled = false,
  haynesProBlocked = false,
  vehicleId,
  vehicleMake,
  vehicleModel,
  pricingType,
  onHaynesProAdopted,
}: LaborItemRowProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { success: notifySuccess, error: notifyError } = useNotify();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [haynesProOpen, setHaynesProOpen] = useState(false);
  // Two-step delete (#3098): the first click swaps the bin for confirm/cancel
  // so a stray click cannot drop a billable step.
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // Local draft for the hours field, committed on blur (#1025): keeps
  // intermediate typing states (`''`, `'0.'`, `'1,5'`) representable instead
  // of parsing every keystroke straight into the numeric estimatedHours.
  const [hoursDraft, setHoursDraft] = useState(() => formatHours(item.estimatedHours));
  const isHoursFocusedRef = useRef(false);
  useEffect(() => {
    if (!isHoursFocusedRef.current) {
      setHoursDraft(formatHours(item.estimatedHours));
    }
  }, [item.estimatedHours]);

  const updateStatusMutation = useUpdateLaborItemStatus();
  const startItemMutation = useStartLaborItem();
  const completeItemMutation = useCompleteLaborItem();
  // The work-order view's "Työ valmis?" gate (#4101): the last open step opens it.
  const requestCompletion = useWorkCompletionRequest();
  const hasPersistedItem = persistedIdPattern.test(item.id);
  const canManageStatus = Boolean(
    workOrderId && serviceId && hasPersistedItem && !statusActionsLocked,
  );

  const handleStatusChange = async (newStatus: LaborItemStatus) => {
    if (!canManageStatus || !workOrderId || !serviceId) return;

    try {
      await updateStatusMutation.mutateAsync({
        workOrderId,
        serviceId,
        itemId: item.id,
        status: newStatus,
      });
      notifySuccess(t('laborItemStatusUpdated'));
    } catch (error) {
      notifyError(describeApiError(error, tc, { fallback: t('laborItemStatusUpdateFailed') }));
    }
  };

  const handleStart = async () => {
    if (!canManageStatus || !workOrderId || !serviceId) return;

    try {
      await startItemMutation.mutateAsync({
        workOrderId,
        serviceId,
        itemId: item.id,
      });
      notifySuccess(t('laborItemStarted'));
    } catch (error) {
      notifyError(describeApiError(error, tc, { fallback: t('laborItemStartFailed') }));
    }
  };

  const handleComplete = async () => {
    if (!canManageStatus || !workOrderId || !serviceId) return;

    const complete = async () => {
      await completeItemMutation.mutateAsync({
        workOrderId,
        serviceId,
        itemId: item.id,
      });
      notifySuccess(t('laborItemCompleted'));
    };

    try {
      if (requestCompletion) {
        await requestCompletion({ kind: 'labor_item', serviceId, itemId: item.id }, complete);
      } else {
        await complete();
      }
    } catch (error) {
      notifyError(describeApiError(error, tc, { fallback: t('laborItemCompleteFailed') }));
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const canStart = item.status === 'pending';
  const canComplete = item.status !== 'completed';
  const canReopen = item.status === 'completed';
  // Discount-aware so the on-screen figure matches what actually reaches the
  // invoice — avoid the display bug where a discount is captured but the
  // shown total isn't (#1815's part-row equivalent, avoided here from the start).
  const hoursCanEdit = isEditable && hoursEditable;
  const hideHourlyPrice = pricingType === 'fixed';
  const itemPriceCents =
    !hideHourlyPrice && laborRateCents != null && laborRateCents > 0 && formatPrice
      ? lineNetAfterDiscountCents(laborRateCents, item.estimatedHours, item.discountPercent)
      : 0;

  // A running step under a paused service is effectively paused: the mechanic
  // stopped, and resuming the service continues this step. The step keeps its
  // own 'in_progress' row status — this is presentation only (#1028).
  const isInheritedPause = item.status === 'in_progress' && serviceStatus === 'paused';
  const isActualTime = item.timeSource === 'actual';
  const isManuallyEdited = item.editedAt != null && !isActualTime;
  const isGuideTime =
    !isManuallyEdited &&
    !isActualTime &&
    (item.timeSource === 'catalog' ||
      item.timeSource === 'vehicle_specific' ||
      item.timeSource === 'provider');
  const canRestore =
    hoursCanEdit &&
    (isManuallyEdited || isActualTime) &&
    item.estimatedHoursOriginal != null &&
    onRestoreHours != null;
  const showHaynesPro =
    haynesProEnabled &&
    hoursCanEdit &&
    hasPersistedItem &&
    Boolean(workOrderId) &&
    Boolean(serviceId) &&
    Boolean(vehicleId);
  const editedWhen = item.editedAt
    ? format(new Date(item.editedAt), 'd.M.yyyy HH:mm', {
        locale: dateFnsLocale(),
      })
    : '';
  const timeSourceTooltip = isActualTime
    ? t('detail.timeSourceActualTooltip', {
        original: formatHours(item.estimatedHoursOriginal ?? item.estimatedHours),
      })
    : isManuallyEdited
      ? t('detail.timeSourceEditedTooltip', {
          original: formatHours(item.estimatedHoursOriginal ?? item.estimatedHours),
          editor: item.editedByName ?? t('statusHistory.system'),
          when: editedWhen,
        })
      : isGuideTime
        ? t(`detail.timeSource.${item.timeSource}`)
        : '';

  return (
    <>
    <Box
      sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
      {...(onRowKeyDown ? { onKeyDown: onRowKeyDown } : {})}
    >
      {/* Status Indicator */}
      <Chip
        size="small"
        label={isInheritedPause ? t('laborItemInheritedPause') : t(statusTranslationKeys[item.status])}
        color={isInheritedPause ? 'warning' : statusColors[item.status]}
        icon={isInheritedPause ? <PauseIcon fontSize="small" /> : statusIcons[item.status]}
        sx={{ minWidth: 80, flexShrink: 0 }}
      />

      {/* Description Field */}
      <TextField
        size="small"
        fullWidth
        placeholder={t('detail.laborDescription')}
        value={item.description}
        onChange={isEditable ? (e) => onUpdate?.(index, 'description', e.target.value) : undefined}
        disabled={!isEditable}
        {...(descriptionInputRef ? { inputRef: descriptionInputRef } : {})}
        sx={{
          '& .MuiInputBase-input': {
            textDecoration: item.status === 'completed' ? 'line-through' : 'none',
            color: item.status === 'completed' ? 'text.secondary' : 'text.primary',
          },
        }}
      />

      {/* Billable hours — estimatedHours is the customer's billed time (#2706). */}
      <TextField
        size="small"
        sx={{ width: 148, flexShrink: 0 }}
        placeholder={t('detail.billableTime')}
        value={hoursDraft}
        onChange={
          hoursCanEdit
            ? (e) => setHoursDraft(e.target.value.replace(/[^0-9.,]/g, ''))
            : undefined
        }
        onFocus={hoursCanEdit ? () => { isHoursFocusedRef.current = true; } : undefined}
        onBlur={
          hoursCanEdit
            ? () => {
                isHoursFocusedRef.current = false;
                const normalized = hoursDraft.replace(',', '.');
                const parsed = parseFloat(normalized);
                if (normalized === '' || Number.isNaN(parsed) || parsed < 0) {
                  // Don't silently coerce an empty/invalid draft into a wrong
                  // number — revert to the last committed value (#1025).
                  setHoursDraft(formatHours(item.estimatedHours));
                  return;
                }
                if (parsed !== item.estimatedHours) {
                  onUpdate?.(index, 'estimatedHours', parsed);
                }
                setHoursDraft(formatHours(parsed));
              }
            : undefined
        }
        disabled={!hoursCanEdit}
        slotProps={{
          htmlInput: { inputMode: 'decimal', 'aria-label': t('detail.billableTime') },
          input: {
            endAdornment: <InputAdornment position="end">h</InputAdornment>,
          },
        }}
      />

      {showHaynesPro && (
        <Tooltip
          title={
            haynesProBlocked
              ? t('detail.haynesProTime.saveFirst')
              : t('detail.haynesProTime.action')
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<TimerOutlinedIcon fontSize="small" />}
              onClick={() => {
                if (haynesProBlocked) return;
                setHaynesProOpen(true);
              }}
              disabled={haynesProBlocked}
              aria-label={t('detail.haynesProTime.actionAria')}
              color="primary"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap', minWidth: 'auto' }}
            >
              {t('detail.haynesProTime.action')}
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Discount Field — parity with the part row's discount (#1814) */}
      <TextField
        size="small"
        type="number"
        sx={{ width: 90, flexShrink: 0 }}
        placeholder={t('detail.laborDiscount')}
        value={item.discountPercent}
        onChange={
          isEditable
            ? (e) => {
                const val = parseFloat(e.target.value) || 0;
                onUpdate?.(index, 'discountPercent', Math.max(0, Math.min(100, val)));
              }
            : undefined
        }
        disabled={!isEditable}
        slotProps={{
          htmlInput: { min: 0, max: 100, step: '0.1' },
          input: {
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          },
        }}
      />

      {(isGuideTime || isManuallyEdited || isActualTime) && (
        <Tooltip title={timeSourceTooltip}>
          <Chip
            size="small"
            variant="outlined"
            color={isManuallyEdited ? 'warning' : isActualTime ? 'info' : 'default'}
            label={
              isActualTime
                ? t('detail.timeSource.actual')
                : isManuallyEdited
                  ? t('detail.timeSourceEdited')
                  : t('detail.timeSourceGuide')
            }
            sx={{ flexShrink: 0, maxWidth: 140 }}
          />
        </Tooltip>
      )}

      {canRestore && (
        <Tooltip title={t('detail.restoreGuideTime')}>
          <IconButton
            size="small"
            onClick={() => onRestoreHours(index)}
            aria-label={t('detail.restoreGuideTime')}
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {itemPriceCents > 0 && formatPrice && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', minWidth: 72, textAlign: 'right' }}
        >
          {formatPrice(itemPriceCents)}
        </Typography>
      )}

      {/* Action Buttons */}
      {canManageStatus && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Quick Start/Complete Buttons */}
          {canStart && (
            <Tooltip title={t('startTask')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleStart}
                  disabled={startItemMutation.isPending}
                  color="primary"
                  aria-label={t('startTask')}
                >
                  <StartIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {canComplete && item.status !== 'completed' && (
            <Tooltip title={t('markAsComplete')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleComplete}
                  disabled={completeItemMutation.isPending}
                  color="success"
                  aria-label={t('markAsComplete')}
                >
                  <CompleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* More Options Menu */}
          <Tooltip title={tc('moreOptions')}>
            <IconButton size="small" onClick={handleMenuClick} aria-label={tc('moreOptions')}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {canStart && (
              <MenuItem
                onClick={() => {
                  handleStart();
                  handleMenuClose();
                }}
                disabled={startItemMutation.isPending}
              >
                {t('startTask')}
              </MenuItem>
            )}
            {canComplete && (
              <MenuItem
                onClick={() => {
                  handleComplete();
                  handleMenuClose();
                }}
                disabled={completeItemMutation.isPending}
              >
                {t('markAsComplete')}
              </MenuItem>
            )}
            {canReopen && (
              <MenuItem
                onClick={() => {
                  // Reopen means "back under way", not "never started" (#1028):
                  // it clears the completion audit but keeps the original start
                  // time, and pulls the parent service back to in_progress.
                  handleStatusChange('in_progress');
                  handleMenuClose();
                }}
                disabled={updateStatusMutation.isPending}
              >
                {t('reopenTask')}
              </MenuItem>
            )}
          </Menu>
        </Box>
      )}

      {/* Delete Button — removing a step changes billable hours (#2706). */}
      {hoursCanEdit && onRemove && !confirmingRemove && (
        <Tooltip title={tc('delete')}>
          <IconButton size="small" onClick={() => setConfirmingRemove(true)} color="error" aria-label={tc('delete')}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {hoursCanEdit && onRemove && confirmingRemove && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={t('detail.confirmRemoveLaborStep')}>
            <IconButton
              size="small"
              onClick={() => {
                setConfirmingRemove(false);
                onRemove(index);
              }}
              color="error"
              aria-label={t('detail.confirmRemoveLaborStep')}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('detail.cancelRemoveLaborStep')}>
            <IconButton
              size="small"
              onClick={() => setConfirmingRemove(false)}
              aria-label={t('detail.cancelRemoveLaborStep')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
    {showHaynesPro && !haynesProBlocked && haynesProOpen && workOrderId && serviceId && vehicleId && (
      <HaynesProWorkOrderTimeDialog
        open={haynesProOpen}
        onClose={() => setHaynesProOpen(false)}
        workOrderId={workOrderId}
        vehicleId={vehicleId}
        vehicleMake={vehicleMake}
        vehicleModel={vehicleModel}
        bookingServiceId={serviceId}
        laborItemId={item.id}
        currentHours={item.estimatedHours}
        isManualOverride={isManuallyEdited || item.timeSource === 'manual'}
        pricingType={pricingType}
        onAdopted={(result) => {
          onHaynesProAdopted?.(result);
          setHaynesProOpen(false);
        }}
      />
    )}
    </>
  );
}
